import {
  Bell,
  BellRing,
  Menu,
  MonitorUp,
  Phone,
  Search,
  Settings,
  UserPlus,
  Video,
} from 'lucide-react';
import type { BrowserNotificationState } from '../hooks/useNotificationPermission';

export default function TopBar({
  title,
  subtitle,
  onBackToInbox,
  onOpenFriends,
  onOpenSettings,
  onRequestNotifications,
  notificationPermission,
  onStartVoice,
  onStartVideo,
  onStartScreenShare,
}: {
  title: string;
  subtitle: string;
  onBackToInbox: () => void;
  onOpenFriends: () => void;
  onOpenSettings: () => void;
  onRequestNotifications: () => void;
  notificationPermission: BrowserNotificationState;
  onStartVoice: () => void;
  onStartVideo: () => void;
  onStartScreenShare: () => void;
}) {
  const notificationsReady = notificationPermission === 'granted';
  const callActions = [
    { label: 'Start voice call', icon: Phone, action: onStartVoice },
    { label: 'Start video call', icon: Video, action: onStartVideo },
    { label: 'Start screen share', icon: MonitorUp, action: onStartScreenShare },
  ];

  return (
    <header className="relay-glass flex h-[72px] shrink-0 items-center gap-3 border-b border-white/10 px-3 sm:px-4">
      <button
        type="button"
        onClick={onBackToInbox}
        className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 bg-white/[0.04] text-slate-300 transition hover:bg-white/10 hover:text-white md:hidden"
        aria-label="Back to conversations"
      >
        <Menu size={18} />
      </button>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="hidden text-slate-500 sm:inline">#</span>
          <h1 className="truncate text-base font-bold tracking-[-0.01em] text-white">{title}</h1>
        </div>
        <p className="truncate text-xs text-slate-500">{subtitle}</p>
      </div>

      <label className="relative hidden min-w-[180px] max-w-xs flex-1 lg:block">
        <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
        <input
          className="h-10 w-full rounded-xl border border-white/10 bg-black/25 pl-9 pr-3 text-sm text-slate-200 placeholder:text-slate-500 transition focus:border-relay-cyan/50"
          placeholder="Search"
        />
      </label>

      <div className="flex items-center gap-1">
        {callActions.map(({ label, icon: Icon, action }, index) => (
          <button
            key={label}
            type="button"
            onClick={action}
            className={`${index === 0 ? 'grid' : 'hidden sm:grid'} h-10 w-10 place-items-center rounded-xl text-slate-400 transition hover:bg-white/10 hover:text-white`}
            title={label}
            aria-label={label}
          >
            <Icon size={18} />
          </button>
        ))}

        <button
          type="button"
          onClick={onOpenFriends}
          className="grid h-10 w-10 place-items-center rounded-xl text-slate-400 transition hover:bg-white/10 hover:text-white"
          title="Friend requests"
          aria-label="Open friend requests"
        >
          <UserPlus size={18} />
        </button>

        <button
          type="button"
          onClick={onRequestNotifications}
          className="grid h-10 w-10 place-items-center rounded-xl text-slate-400 transition hover:bg-white/10 hover:text-white"
          title={notificationsReady ? 'Notifications enabled' : 'Enable notifications'}
          aria-label={notificationsReady ? 'Notifications enabled' : 'Enable notifications'}
        >
          {notificationsReady ? <BellRing size={18} className="text-relay-cyan" /> : <Bell size={18} />}
        </button>

        <button
          type="button"
          onClick={onOpenSettings}
          className="grid h-10 w-10 place-items-center rounded-xl text-slate-400 transition hover:bg-white/10 hover:text-white"
          title="Settings"
          aria-label="Open settings"
        >
          <Settings size={18} />
        </button>
      </div>
    </header>
  );
}
